import { environment } from '../../../environments/environment';

/**
 * OPS-02-T03: samples the Core Web Vitals of a share of page loads with the browser's own PerformanceObserver (no library) and
 * sends them once, when the page is hidden, to api/ClientMetrics (Grafana "ERP SLO" dashboard). Only the vital, its value,
 * its rating and the route are sent — nothing about the user.
 */
export const WEB_VITALS_SAMPLE_RATE = 0.1;

export type VitalName = 'LCP' | 'INP' | 'CLS' | 'FCP' | 'TTFB';
export interface Vital { name: VitalName; value: number; rating: 'good' | 'needs-improvement' | 'poor'; route: string; }

/** web.dev thresholds: [good up to, poor above]. */
const THRESHOLDS: Record<VitalName, [number, number]> = {
  LCP: [2500, 4000], INP: [200, 500], CLS: [0.1, 0.25], FCP: [1800, 3000], TTFB: [800, 1800],
};

export function rate(name: VitalName, value: number): Vital['rating'] {
  const [good, poor] = THRESHOLDS[name];
  return value <= good ? 'good' : value > poor ? 'poor' : 'needs-improvement';
}

/** Cumulative layout shift: the largest session window (shifts less than 1 s apart, a window at most 5 s). */
export function clsOf(shifts: { value: number; startTime: number }[]): number {
  let max = 0, current = 0, first = 0, last = 0;
  for (const s of shifts) {
    if (current && s.startTime - last < 1000 && s.startTime - first < 5000) current += s.value;
    else { current = s.value; first = s.startTime; }
    last = s.startTime;
    max = Math.max(max, current);
  }
  return max;
}

function observe(type: string, callback: (entries: any[]) => void, options: Record<string, unknown> = {}) {
  try {
    const observer = new PerformanceObserver(list => callback(list.getEntries()));
    observer.observe({ type, buffered: true, ...options } as PerformanceObserverInit);
  } catch { /* the browser does not support this entry type */ }
}

/** Starts measuring (sampled); safe to call once at start-up. Answers whether this page load is sampled. */
export function startWebVitals(sampleRate = WEB_VITALS_SAMPLE_RATE, send: (vitals: Vital[]) => void = sendVitals): boolean {
  if (typeof window === 'undefined' || typeof PerformanceObserver === 'undefined' || Math.random() >= sampleRate) return false;
  let lcp = 0, fcp = 0, inp = 0;
  const shifts: { value: number; startTime: number }[] = [];
  observe('largest-contentful-paint', e => { if (e.length) lcp = e[e.length - 1].startTime; });
  observe('paint', e => { const p = e.find(x => x.name === 'first-contentful-paint'); if (p) fcp = p.startTime; });
  observe('layout-shift', e => e.forEach(x => { if (!x.hadRecentInput) shifts.push({ value: x.value, startTime: x.startTime }); }));
  // INP: the slowest interaction of the page view (a fair estimate for one view).
  observe('event', e => e.forEach(x => { if (x.interactionId) inp = Math.max(inp, x.duration); }), { durationThreshold: 40 });
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  const ttfb = nav ? Math.max(0, nav.responseStart) : 0;

  let sent = false;
  const flush = () => {
    if (sent || document.visibilityState !== 'hidden') return;
    sent = true;
    const route = location.pathname;
    const values: [VitalName, number][] = [['LCP', lcp], ['FCP', fcp], ['TTFB', ttfb], ['CLS', clsOf(shifts)], ['INP', inp]];
    const vitals = values.filter(([name, v]) => v > 0 || name === 'CLS')
      .map(([name, value]) => ({ name, value: Math.round(value * (name === 'CLS' ? 10000 : 1)) / (name === 'CLS' ? 10000 : 1), rating: rate(name, value), route }));
    if (vitals.length) send(vitals);
  };
  document.addEventListener('visibilitychange', flush);
  return true;
}

/** keepalive: the request survives the page being closed (sendBeacon cannot send JSON across origins). */
function sendVitals(vitals: Vital[]) {
  try {
    fetch(`${environment.apiUrl}ClientMetrics`, {
      method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(vitals),
    }).catch(() => undefined);
  } catch { /* never break the page for telemetry */ }
}
